import { useTheme } from '@emotion/react';
import type { FC } from 'react';

import CheckIcon from '@/assets/svg/ui-elements/check-outline.svg';

import Typography from '../Typography';
import { deriveState } from './derive-state';
import {
  BAR_HEIGHT,
  HORIZONTAL_INDICATOR_SIZE,
  HorizontalItem,
  HorizontalRoot,
  HorizontalStep,
  horizontalTone,
  StepBar,
  StepContent,
  StepIndicator,
  StepLabel,
} from './Stepper.styled';
import type { HorizontalStepperProps, StepperStepState } from './types';

const ORDER: { before: StepperStepState; at: StepperStepState; after: StepperStepState } = {
  before: 'completed',
  at: 'in-progress',
  after: 'not-started',
};

export const HorizontalStepper: FC<HorizontalStepperProps> = ({
  steps,
  value,
  onStepClick,
  size = 'lg',
  labels = 'all',
  ...rest
}) => {
  const theme = useTheme();
  const selectedIndex = steps.findIndex((step) => step.key === value);
  const interactive = Boolean(onStepClick);

  return (
    <HorizontalRoot {...rest}>
      {steps.map((step, index) => {
        const selected = index === selectedIndex;
        const state = deriveState(step.state, index, selectedIndex, ORDER);
        const tone = horizontalTone(theme, state, selected);
        const showLabel = labels === 'all' || selected;

        return (
          <HorizontalItem key={step.key} $fit={labels === 'selected' && selected}>
            <HorizontalStep
              as={interactive ? 'button' : 'div'}
              type={interactive ? 'button' : undefined}
              $interactive={interactive}
              aria-current={selected ? 'step' : undefined}
              onClick={interactive ? () => onStepClick?.(step.key) : undefined}
            >
              <StepContent>
                <StepIndicator
                  data-part="indicator"
                  $size={HORIZONTAL_INDICATOR_SIZE}
                  $fill={tone.fill}
                  $onFill={tone.onFill}
                >
                  {state === 'completed' ? (
                    <CheckIcon aria-hidden="true" />
                  ) : (
                    <Typography as="span" variant="body4" color="inherit">
                      {index + 1}
                    </Typography>
                  )}
                </StepIndicator>

                {showLabel && (
                  <StepLabel $noWrap={labels === 'selected'}>
                    <Typography
                      as="span"
                      variant={size === 'lg' ? 'body2' : 'body3'}
                      fontWeight={500}
                      color={theme.colors.text.primary}
                    >
                      {step.label}
                    </Typography>
                  </StepLabel>
                )}
              </StepContent>

              <StepBar data-part="progress" $fill={tone.fill} $height={BAR_HEIGHT[size]} />
            </HorizontalStep>
          </HorizontalItem>
        );
      })}
    </HorizontalRoot>
  );
};
